import clsx from 'clsx'
import './timepicker.scss'
import { useEffect, useRef, useState } from 'react'
import icClock from './ic-clock.svg'
import icXGrey from '../../../assets/images/ic-x-grey.svg'
import moment from 'moment'
import { useClickOutside } from 'hooks/useClickOutside'

const Timepicker = ({
    className,
    name = 'avo-time-picker',
    placeholder = 'hh:mm',
    value,
    disabled,
    onChange,
    isOnlyHour,
}) => {
    const inputRef = useRef()
    const regexValidValue = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
    const [isOnFocus, setIsOnFocus] = useState(false)
    const [sValue, setValue] = useState('')
    const [show, setShow] = useState(false)
    const [selectedTime, setSelectedTime] = useState([])
    const [position, setPosition] = useState('below')
    const [hours, setHours] = useState([])
    const [minutes, setMinutes] = useState([])
    const timeRef = {
        picker: useRef(),
        options: useRef(),
        hours: useRef(),
        minutes: useRef(),
    }

    const getNumber = (length) => {
        const numbers = []
        for (let i = 0; i < length; i++) {
            let newNumber = i.toString()
            if (newNumber?.length === 1) newNumber = `0${i}`
            numbers.push(newNumber)
        }
        return numbers
    }

    const handleOnChange = (e) => {
        const { name, value } = e.target
        setValue(value)
        if (onChange) onChange({ name, value })
    }

    const handleOnFocus = () => {
        setIsOnFocus(true)
        inputRef.current.type = 'time'
    }

    const handleOnBlur = () => {
        setIsOnFocus(false)
        inputRef.current.type = 'text'
    }

    const handleClickIconClock = () => {
        if (isOnlyHour) return
        handleOnFocus()
        inputRef.current.focus()
        setShow(true)
    }

    const handleClickIconClose = () => {
        setValue('')
        if (onChange) onChange({ name, value: null })
    }

    const calculatePosition = () => {
        const { bottom } = timeRef.picker?.current?.getBoundingClientRect()
        const windowHeight = window.innerHeight
        const optionsHeight = timeRef.options?.current?.offsetHeight
        const spaceBelow = windowHeight - bottom

        if (spaceBelow >= optionsHeight) {
            setPosition('below')
        } else {
            setPosition('above')
        }
    }

    const setTime = (type) => {
        if (type === 'hours') {
            const newHours = hours?.length < 48 ? [...hours, ...getNumber(24)] : [...hours?.splice(0, 24)]
            setHours(newHours)
        } else {
            const newMinutes = minutes?.length < 120 ? [...minutes, ...getNumber(60)] : [...minutes?.splice(0, 60)]
            setMinutes(newMinutes)
        }
    }

    const onScroll = (event, type) => {
        event?.preventDefault()
        const { scrollTop, scrollHeight, clientHeight } = event?.target
        const isNearBottom = scrollTop + clientHeight >= scrollHeight

        if (isNearBottom) {
            setTime(type)
        } else if (scrollTop === 0) {
            setTime(type)
            timeRef?.[type]?.current?.scrollTo(0, scrollHeight / 2)
        }
    }

    const onClickTime = (value, type) => {
        const currentMinute = [...selectedTime]
        if (type === 'hour') {
            currentMinute[0] = value
        } else {
            currentMinute[1] = value
        }
        setSelectedTime(currentMinute)
        setValue(currentMinute?.join(':'))
        if (onChange) onChange({ name, value: currentMinute?.join(':') })
    }

    useEffect(() => {
        const initValue = regexValidValue.test(value) ? value : ''
        setValue(initValue)
    }, [value])

    useEffect(() => {
        setHours([...getNumber(24), ...getNumber(24)])
        setMinutes([...getNumber(60), ...getNumber(60)])
    }, [])

    useEffect(() => {
        const currentTime = moment().format('HH:mm')
        setSelectedTime(currentTime?.split(':'))
        calculatePosition()
    }, [show])

    useClickOutside({
        callback: () => setShow(false),
        elementID: `time-picker-${name}`,
    })

    return (
        <div ref={timeRef?.picker} className={clsx('avo__timepicker', className)}>
            <input
                className={'input-timepicker'}
                name={name}
                step={isOnlyHour && '3600'}
                type='text'
                placeholder={placeholder}
                value={sValue}
                ref={inputRef}
                onChange={handleOnChange}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                disabled={disabled}
            />
            {!isOnFocus && sValue?.length > 0 && !disabled ? (
                <img src={icXGrey} alt='ic-x' className='icon-close' onClick={handleClickIconClose} />
            ) : (
                <img
                    src={icClock}
                    alt='ic-clock'
                    className={clsx('icon-clock', disabled && 'disabled')}
                    onClick={!disabled ? handleClickIconClock : () => {}}
                />
            )}
            <div
                ref={timeRef?.options}
                id={`time-picker-${name}`}
                className={clsx('options-time', position, show ? 'show' : 'hide')}
            >
                <div ref={timeRef?.hours} onScroll={(event) => onScroll(event, 'hours')} className={'hours'}>
                    {hours?.map((item, index) => (
                        <div
                            className={clsx('item', item === selectedTime[0] && 'selected')}
                            key={index}
                            onClick={() => onClickTime(item, 'hour')}
                        >
                            {item}
                        </div>
                    ))}
                </div>
                <div className={'line'}></div>
                <div ref={timeRef?.minutes} onScroll={(event) => onScroll(event, 'minutes')} className={'minutes'}>
                    {minutes?.map((item, index) => (
                        <div
                            className={clsx('item', item === selectedTime[1] && 'selected')}
                            key={index}
                            onClick={() => onClickTime(item, 'minute')}
                        >
                            {item}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Timepicker
